/* eslint-disable max-lines */
import { useAIRecommendation } from '@hakimo-ui/hakimo/data-access';
import {
  CamAction,
  DataVersion,
  SOPViewType,
  StatusType,
  UnifiedAlarmGroup,
  UnifiedMonitoringProduct,
} from '@hakimo-ui/hakimo/types';
import { SpecialZoneType } from '@hakimo-ui/hakimo/ui-elements';
import {
  isEscalationInScope,
  selectActiveEscalation,
  trackEvent,
  useFullscreen,
  usePrevious,
  withErrorBoundary,
} from '@hakimo-ui/hakimo/util';
import clsx from 'clsx';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import LocationContacts from '../location-contacts/LocationContacts';
import { SOP as LocationSOP } from '../location-sop/SOP';
import { EnterpriseAlarmCard } from '../sop-workflow/EnterpriseAlarmCard';
import { AIRecommendation } from '../sop-workflow/ai-recommendation';
import { UnauthorizedFaceDetection } from '../sop-workflow/unauthorized-face-detection/UnauthorizedFaceDetection';
import {
  extractUnauthorizedFaces,
  useShouldHideAIRecommendation,
} from '../sop-workflow/utils';
import AlarmGroupMedia from './alarm-group-media/AlarmGroupMedia';
import { AlarmGroupProvider } from './alarm-provider/AlarmGroupProvider';
import AlarmUpdates from './alarm-updates/AlarmUpdates';
import AlarmGroupToolbar from './controls/AlarmGroupToolbar';
import Floorplan from './Floorplan';
import { AlarmGroupHeader } from './header/AlarmGroupHeader';
import { CameraFeedMode, EscalationState } from './types';
import UnifiedSOPWrapper from './UnifiedSOPWrapper';
import { normalizeAlarmGroupLocation } from './utils';

interface Props {
  alarmGroup: UnifiedAlarmGroup;
  product: UnifiedMonitoringProduct;
  sopViewType?: SOPViewType;
  escalationState?: EscalationState;
  onResolveEscalation?: (
    comment?: string,
    reason?: string,
    category?: string
  ) => void;
  onCreateEscalation?: (
    comment?: string,
    situation?: string,
    subSituation?: string
  ) => void;
  onCloseInvestigateModal?: () => void;
  handleCamAction?: (
    actionType: CamAction
  ) => (comment?: string, action?: string, subAction?: string) => void;
  addTemporaryResolvedState?: (alarmGroupId: string) => void; //TODO: remove this
  getDisplayStatus?: (alarmGroupId: string, actualStatus: string) => string; //TODO: remove this
  onResolveAlarm?: () => void;
  siblingEscalations?: EscalationState[];

  onCloseAlarm?: () => void;
  showCloseAlarmButton?: boolean;
  /** See AlarmGroupStaticContextType.dataVersion. */
  dataVersion?: DataVersion;
}

const AlarmGroupContainerComponent = function ({
  alarmGroup,
  product,
  sopViewType = SOPViewType.WORKFLOW,
  escalationState,
  onResolveEscalation,
  onCreateEscalation,
  onCloseInvestigateModal,
  handleCamAction,
  addTemporaryResolvedState,
  getDisplayStatus,
  onResolveAlarm,
  siblingEscalations,
  onCloseAlarm,
  showCloseAlarmButton,
  dataVersion,
}: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const { isFullScreen, toggleFullScreen } = useFullscreen(containerRef);
  const prevIsFullScreen = usePrevious(isFullScreen);
  useEffect(() => {
    if (prevIsFullScreen !== undefined && prevIsFullScreen !== isFullScreen) {
      trackEvent('alarm_group_toggle_fullscreen', {
        alarmGroupId: alarmGroup.id,
        product,
        isFullScreen,
      });
    }
  }, [isFullScreen, prevIsFullScreen, alarmGroup.id, product]);
  const [globalCamMode, setGlobalCamMode] = useState<CameraFeedMode>(
    CameraFeedMode.ALARM_VIDEO
  );
  const [visibleZoneTypes, setVisibleZoneTypes] = useState<
    Set<SpecialZoneType>
  >(() => new Set<SpecialZoneType>());

  const { data: aiData } = useAIRecommendation(alarmGroup.id, product);

  const aiOperatorRep = aiData ? aiData[0] : undefined;

  const unauthorizedFaces = useMemo(
    () => extractUnauthorizedFaces(alarmGroup),
    [alarmGroup]
  );

  const hideDetailsHook = useShouldHideAIRecommendation(
    alarmGroup.id,
    product,
    unauthorizedFaces
  );

  // For STATIC view: show ESCALATE badge when recommendation is RESOLVE and there are unauthorized faces
  // This applies regardless of escalation status
  const shouldHideAIRecommendation =
    sopViewType === SOPViewType.STATIC && hideDetailsHook;

  const onAlarmResolve = useCallback(
    (status: StatusType) => {
      if (status === 'Resolved') {
        onResolveAlarm?.();
      }
    },
    [onResolveAlarm]
  );

  const onChangeGlobalCamMode = useCallback(
    (mode: CameraFeedMode) => setGlobalCamMode(mode),
    []
  );
  const onToggleVisibleZoneType = useCallback((zoneType: SpecialZoneType) => {
    setVisibleZoneTypes((prev) => {
      const next = new Set(prev);
      if (next.has(zoneType)) {
        next.delete(zoneType);
      } else {
        next.add(zoneType);
      }
      return next;
    });
  }, []);

  const adaptedLocation = useMemo(
    () => normalizeAlarmGroupLocation(alarmGroup.location, alarmGroup.tenantId),
    [alarmGroup.location, alarmGroup.tenantId]
  );

  const cameraNames = useMemo(
    () => [...new Set(alarmGroup.alarms.map((alarm) => alarm.cameraName))],
    [alarmGroup.alarms]
  );

  // `escalations` is every incident at the location, so narrow to the ones
  // scoped to this group before choosing which drives the banner -- otherwise
  // another group's (or another alarm type's) live incident can win.
  const escalation = useMemo(
    () =>
      selectActiveEscalation(
        (alarmGroup.escalations ?? []).filter((item) =>
          isEscalationInScope(item, {
            alarmGroupId: alarmGroup.id,
            sourceGroupId: alarmGroup.sourceGroupId,
            locationAlarmTypeId: alarmGroup.locationAlarmTypeId,
            product,
          })
        )
      ),
    [
      alarmGroup.escalations,
      alarmGroup.id,
      alarmGroup.sourceGroupId,
      alarmGroup.locationAlarmTypeId,
      product,
    ]
  );

  const handleEscalationFromSOP = useCallback(
    (comment: string) => {
      onCreateEscalation?.(comment);
    },
    [onCreateEscalation]
  );

  return (
    <AlarmGroupProvider
      key={alarmGroup.id}
      alarmGroupId={alarmGroup.id}
      alarms={alarmGroup.alarms}
      location={adaptedLocation}
      product={product}
      tapScore={alarmGroup.tapScore}
      timestamp={alarmGroup.timestamp}
      locationAlarmType={alarmGroup.locationAlarmType}
      dataVersion={dataVersion}
      sourceGroupId={alarmGroup.sourceGroupId}
    >
      <div
        ref={containerRef}
        id="alarm-group-container"
        className={clsx(
          'bg-hakimo-light-bg dark:bg-hakimo-dark-bg flex h-full overflow-hidden',
          isFullScreen && 'p-4',
          sopViewType === SOPViewType.WORKFLOW && 'gap-0.5'
        )}
      >
        {/* Main Content Area - Scrollable container */}
        <div
          className={clsx(
            'flex min-w-0 flex-col gap-2 overflow-y-auto pb-2',
            sopViewType === SOPViewType.WORKFLOW ? 'w-3/4' : 'flex-1'
          )}
        >
          {/* Header - Scrolls with page */}
          <div className="sticky top-0 z-20 flex-none">
            <AlarmGroupHeader
              alarmGroupId={alarmGroup.id}
              timestamp={alarmGroup.timestamp}
              alarmStatus={alarmGroup.status}
              location={adaptedLocation}
              globalCamMode={globalCamMode}
              onChangeGlobalCamMode={onChangeGlobalCamMode}
              visibleZoneTypes={visibleZoneTypes}
              onToggleVisibleZoneType={onToggleVisibleZoneType}
              isFullScreen={isFullScreen}
              toggleFullScreen={toggleFullScreen}
              escalation={escalation}
              product={product}
              scanEscalationState={escalationState}
              onResolveEscalation={onResolveEscalation}
              resolution={alarmGroup.resolution}
              showResolveAction={sopViewType === SOPViewType.STATIC}
              cameraNames={cameraNames}
              addTemporaryResolvedState={addTemporaryResolvedState}
              getDisplayStatus={getDisplayStatus}
              siblingEscalations={siblingEscalations}
              aiOperatorRep={aiOperatorRep}
              onAlarmResolve={onAlarmResolve}
              onCloseAlarm={onCloseAlarm}
              showCloseAlarmButton={showCloseAlarmButton}
              abTestConfig={alarmGroup.abTests}
            />
          </div>

          {/* Media + Toolbar Section - Fills remaining viewport initially, scrolls with page */}
          <div className="bg-onlight-bg-1 dark:bg-ondark-bg-1 mx-auto flex min-h-[calc(100vh-5rem)] w-[95%] flex-none flex-col rounded-lg">
            {/* Media - Takes available space */}
            <div className="h-full flex-1">
              <AlarmGroupMedia
                globalCamMode={globalCamMode}
                visibleZoneTypes={visibleZoneTypes}
              />
            </div>

            {/* Toolbar - Fixed height at bottom of media */}
            <div
              className={clsx(
                'flex-none',
                sopViewType === SOPViewType.STATIC && 'pr-20'
              )}
            >
              <AlarmGroupToolbar />
            </div>
          </div>

          {/* Below-the-fold Content - WORKFLOW variant */}
          {sopViewType === SOPViewType.WORKFLOW && (
            <div className="mx-auto grid w-[95%] grid-cols-2 gap-2">
              <Floorplan locationId={String(adaptedLocation.id)} />
              <AlarmUpdates
                locationAlarmId={alarmGroup.id}
                locationName={adaptedLocation.name}
                alarmGroupTimestamp={alarmGroup.timestamp}
                product={product}
                locationTimezone={adaptedLocation.timezone}
                title="Alarm Group Updates"
              />
            </div>
          )}

          {/* Below-the-fold Content - STATIC variant */}
          {sopViewType === SOPViewType.STATIC && (
            <div className="bg-hakimo-light-bg dark:bg-hakimo-dark-bg mx-auto grid w-[95%] grid-cols-2 gap-2">
              {/* Left Column */}
              <div className="flex flex-col gap-2">
                {/* AI Recommendation Card */}
                <div className="bg-onlight-bg-1 dark:bg-ondark-bg-1 rounded-lg p-4">
                  <h2 className="mb-2 text-base font-bold">
                    AI Recommendation
                  </h2>
                  <AIRecommendation
                    alarmId={alarmGroup.id}
                    product={product}
                    timeZone={adaptedLocation.timezone}
                    hideDetails={shouldHideAIRecommendation}
                  />
                  <EnterpriseAlarmCard timeZone={adaptedLocation.timezone} />
                  {unauthorizedFaces.length > 0 && (
                    <UnauthorizedFaceDetection
                      faces={unauthorizedFaces}
                      timeZone={adaptedLocation.timezone}
                    />
                  )}
                </div>

                {/* Alarm Updates Card */}
                <div>
                  <AlarmUpdates
                    locationAlarmId={alarmGroup.id}
                    locationName={adaptedLocation.name}
                    alarmGroupTimestamp={alarmGroup.timestamp}
                    product={product}
                    locationTimezone={adaptedLocation.timezone}
                    title="Alarm Group Updates"
                  />
                </div>

                {/* Location Contacts Card */}
                <div className="bg-onlight-bg-1 dark:bg-ondark-bg-1 overflow-hidden rounded-lg p-4">
                  <h2 className="mb-2 text-base font-bold">
                    Location Contacts
                  </h2>
                  <LocationContacts
                    locationId={adaptedLocation.id}
                    locationTenantId={adaptedLocation.tenant_id}
                    showTitle={false}
                    locationAlarmId={alarmGroup.id}
                    alarmGroupId={alarmGroup.id}
                    product={product}
                    timestamp={alarmGroup.timestamp}
                  />
                </div>

                {/* Floorplan Card */}
                <div>
                  <Floorplan locationId={String(adaptedLocation.id)} />
                </div>
              </div>

              {/* Right Column - SOP */}
              <div>
                <LocationSOP
                  locationId={String(adaptedLocation.id)}
                  tenantId={adaptedLocation.tenant_id}
                />
              </div>
            </div>
          )}
        </div>

        {/* SOP Sidebar - WORKFLOW variant only (fixed, does not scroll) */}
        {sopViewType === SOPViewType.WORKFLOW && (
          <div className="h-full w-1/4 min-w-0 flex-none overflow-hidden">
            <UnifiedSOPWrapper
              product={product}
              alarmGroup={alarmGroup}
              escalation={escalation}
              locationId={String(adaptedLocation.id)}
              tenantId={adaptedLocation.tenant_id ?? ''}
              escalationState={escalationState}
              onResolveEscalation={onResolveEscalation}
              onCreateEscalation={onCreateEscalation}
              handleCamAction={handleCamAction}
              onCloseInvestigateModal={onCloseInvestigateModal}
              alarmGroupStatus={alarmGroup.status}
              timeZone={adaptedLocation.timezone}
              onAlarmResolve={onAlarmResolve}
              onEscalate={handleEscalationFromSOP}
            />
          </div>
        )}
      </div>
    </AlarmGroupProvider>
  );
};

export const AlarmGroupContainer = withErrorBoundary(
  AlarmGroupContainerComponent
);
